import axios from "axios";

const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': process.env.REACT_APP_API_KEY,
};

const getAll = async (page, pageSize, name, data_inizio, data_fine, selectedTag, selectedSubjectTag, luogo_conservazione, selectedLanguage, freeSearch) => {
  try {
    const data = JSON.stringify({
      "jsonrpc": "2.0",
      "params": {
        "data": {
          'page': page,
          'page_size': pageSize,
          'name': name,
          'data_inizio': data_inizio,
          'data_fine': data_fine,
          'selectedTag': selectedTag,
          'selectedSubjectTag': selectedSubjectTag,
          'luogo_conservazione': luogo_conservazione,
          'language': selectedLanguage,
          'freeSearch': freeSearch,
        }
      }
    });

    const config = {
      timeout: 20000,
      method: 'POST',
      url: '/api/artwork/getAll',
      headers: API_HEADERS,
      data: data
    };

    const response = await axios(config);

    if (response && response.data && response.data.result) {
      return {
        artworks: response.data.result.response,
        totalArtworksCount: response.data.result.totalArtworksCount,
      };
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error) {
    throw new Error("Failed to get all artworks: " + error.message);
  }
};

const get = async (id) => {
  try {
    const data = JSON.stringify({
      "jsonrpc": "2.0",
      "method": "call",
      "params": {}
    });

    const config = {
      timeout: 20000,
      method: 'POST',
      url: `/api/artwork/get/${id}`,
      headers: API_HEADERS,
      data: data
    };

    const response = await axios(config);

    if (response && response.data && response.data.result) {
      return response.data.result;
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error) {
    throw new Error("Error getting artwork by ID");
  }
};

const getTags = async () => {
  try {
    const data = JSON.stringify({
      "jsonrpc": "2.0",
      "method": "call",
      "params": {}
    });

    const config = {
      timeout: 20000,
      method: 'POST',
      url: '/api/artwork/getTags',
      headers: API_HEADERS,
      data: data
    };

    const response = await axios(config);

    if (response && response.data && response.data.result) {
      return response.data.result;
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error) {
    throw new Error("Error getting artwork tag");
  }
};


const getSubjectTags = async () => {
  try {
    const data = JSON.stringify({
      "jsonrpc": "2.0",
      "method": "call",
      "params": {}
    });

    const config = {
      timeout: 20000,
      method: 'POST',
      url: '/api/artwork/getSubjectTags',
      headers: API_HEADERS,
      data: data
    };

    const response = await axios(config);

    if (response && response.data && response.data.result) {
      return response.data.result;
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error) {
    throw new Error("Error getting subject tags");
  }
};

const semanticSearch = async (query, selectedLanguage, limit = 15) => {
  try {
    const data = JSON.stringify({
      "jsonrpc": "2.0",
      "method": "call",
      "params": {
        "data": {
          'query': query,
          'language': selectedLanguage,
          'limit': limit,
        }
      }
    });

    const config = {
      timeout: 30000,
      method: 'POST',
      url: '/api/artwork/semanticSearch',
      headers: API_HEADERS,
      data: data
    };

    const response = await axios(config);

    if (response && response.data && response.data.result) {
      return {
        artworks: response.data.result.response || [],
        totalArtworksCount: response.data.result.totalArtworksCount || 0,
      };
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error) {
    throw new Error("Semantic search failed: " + error.message);
  }
};

const getDateHistogram = async () => {
  try {
    const data = JSON.stringify({
      "jsonrpc": "2.0",
      "method": "call",
      "params": {}
    });

    const config = {
      timeout: 20000,
      method: 'POST',
      url: '/api/artwork/getDateHistogram',
      headers: API_HEADERS,
      data: data
    };

    const response = await axios(config);

    if (response && response.data && response.data.result) {
      return response.data.result.response || [];
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error) {
    throw new Error("Error getting date histogram");
  }
};

const staleWhileRevalidate = async (cacheKey, fetchFn, onFresh) => {
  let staleData = null;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      staleData = JSON.parse(cached);
    }
  } catch (e) { /* ignora */ }

  const freshPromise = fetchFn().then(result => {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (e) { /* quota exceeded */ }
    return result;
  });

  if (staleData) {
    freshPromise.then(freshData => {
      if (onFresh) onFresh(freshData);
    }).catch(() => {});
    return staleData;
  }

  return freshPromise;
};

const getTagsSWR = async (onFresh) => {
  return staleWhileRevalidate('swr_tags', getTags, onFresh);
};

const getSubjectTagsSWR = async (onFresh) => {
  return staleWhileRevalidate('swr_subject_tags', getSubjectTags, onFresh);
};

const getAllSWR = async (page, pageSize, name, data_inizio, data_fine,
    selectedTag, selectedSubjectTag, luogo_conservazione, selectedLanguage, freeSearch, onFresh) => {
  const cacheKey = `swr_artworks_${page}_${pageSize}_${selectedLanguage}_${name}_${data_inizio}_${data_fine}_${selectedTag}_${selectedSubjectTag}_${luogo_conservazione}_${freeSearch}`;
  const fetchFn = () => getAll(page, pageSize, name, data_inizio, data_fine,
    selectedTag, selectedSubjectTag, luogo_conservazione, selectedLanguage, freeSearch);
  return staleWhileRevalidate(cacheKey, fetchFn, onFresh);
};

const artworkService = {
  getAll,
  semanticSearch,
  getDateHistogram,
  getAllSWR,
  get,
  getTags,
  getTagsSWR,
  getSubjectTags,
  getSubjectTagsSWR,
};

export default artworkService;
